'use client';

import React, { useEffect, useRef, useState } from 'react';

type WaveformProps = {
  analyser: AnalyserNode | null;
  dataArray: Uint8Array | null;
  bufferLength: number;
  isRecording: boolean;
};

export const Waveform: React.FC<WaveformProps> = ({ analyser, dataArray, bufferLength, isRecording }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationIdRef = useRef<number | null>(null);
  const lastBarTimeRef = useRef<number>(0);
  const barsRef = useRef<{ height: number; x: number; isGray: boolean }[]>([]);

  const recordingDurationSeconds = 30;
  const targetWidthRems = 42; // 42rem ~ 30 seconds
  const canvasHeight = 28;
  const barWidth = 2;
  const gap = 1;
  const scrollSpeed = 0.5;

  const [canvasWidth, setCanvasWidth] = useState(0);
  const [barInterval, setBarInterval] = useState(50);

  useEffect(() => {
    if (!canvasWidth) return;
    const totalBarWidth = barWidth + gap;
    const maxBars = Math.floor(canvasWidth / totalBarWidth);
    const offset = canvasWidth - maxBars * totalBarWidth; // right-align bars within canvas
    barsRef.current = Array.from({ length: maxBars }, (_, i) => ({
      height: barWidth,
      x: offset + i * totalBarWidth,
      isGray: true,
    }));
  }, [canvasWidth]);

  useEffect(() => {
    if (canvasWidth > 0) {
      const widthInRems = canvasWidth / 16;
      const secondsForWidth = (widthInRems / targetWidthRems) * recordingDurationSeconds;
      const totalBarWidth = barWidth + gap;
      const maxBars = Math.floor(canvasWidth / totalBarWidth);
      const timePerBar = (secondsForWidth * 1000) / maxBars;
      setBarInterval(timePerBar < 20 ? 20 : timePerBar);
    }
  }, [canvasWidth]);

  useEffect(() => {
    function updateWidth() {
      if (containerRef.current) setCanvasWidth(containerRef.current.offsetWidth);
    }
    updateWidth();
    window.addEventListener('resize', updateWidth);
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => updateWidth());
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener('resize', updateWidth);
      if (resizeObserver && containerRef.current) resizeObserver.unobserve(containerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!analyser || !canvasRef.current || !dataArray || !canvasWidth) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (canvas.width !== canvasWidth) canvas.width = canvasWidth;
    if (canvas.height !== canvasHeight) canvas.height = canvasHeight;

    function draw(now: number) {
      if (!analyser || !dataArray || !ctx) return;
      animationIdRef.current = requestAnimationFrame(draw);

      if (!lastBarTimeRef.current) lastBarTimeRef.current = now;
      if (now - lastBarTimeRef.current >= barInterval) {
        analyser.getByteTimeDomainData(dataArray as Uint8Array<ArrayBuffer>);
        let sum = 0;
        let maxValue = 0;
        for (let i = 0; i < bufferLength; i++) {
          const value = Math.abs(dataArray[i] - 128);
          sum += value;
          maxValue = Math.max(maxValue, value);
        }
        const averageAmplitude = sum / bufferLength;
        const peakAmplitude = maxValue;
        const amplitude = Math.max(averageAmplitude, peakAmplitude);
        const silenceThreshold = 3;
        const isSilent = amplitude < silenceThreshold;
        const newBarHeight = isSilent ? barWidth : Math.max((amplitude / 25) * canvasHeight, barWidth);

        // Add a new bar at the visible right edge for immediate visibility
        barsRef.current.push({ height: newBarHeight, x: canvasWidth - barWidth, isGray: false });
        lastBarTimeRef.current = now;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Slide bars left over time and draw
      barsRef.current = barsRef.current.filter((bar) => {
        bar.x -= scrollSpeed;
        if (bar.x + barWidth < 0) return false;
        ctx.fillStyle = bar.isGray ? '#777' : '#374151'; // gray-700 for active bars
        ctx.fillRect(bar.x, (canvasHeight - bar.height) / 2, barWidth, bar.height);
        return true;
      });
    }

    if (isRecording) {
      lastBarTimeRef.current = 0;
      // Keep gray placeholders and start drawing immediately
      animationIdRef.current = requestAnimationFrame(draw);
    } else if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
      const totalBarWidth = barWidth + gap;
      const maxBars = Math.floor(canvasWidth / totalBarWidth);
      const offset = canvasWidth - maxBars * totalBarWidth; // right-align reset
      barsRef.current = Array.from({ length: maxBars }, (_, i) => ({
        height: barWidth,
        x: offset + i * totalBarWidth,
        isGray: true,
      }));
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      barsRef.current.forEach((bar) => {
        ctx.fillStyle = '#777';
        ctx.fillRect(bar.x, (canvasHeight - bar.height) / 2, barWidth, bar.height);
      });
    }

    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
    };
  }, [analyser, dataArray, bufferLength, isRecording, canvasWidth, barInterval]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: `${canvasHeight}px`, flex: 1, minWidth: 0 }}>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: `${canvasHeight}px`, background: 'transparent', display: 'block' }}
      />
    </div>
  );
};
